import React, { useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Row,
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import PurchaseService from "../../services/OrderingServices/PurchaseService";
import PurchasesServices from "../../services/Inventory Services/PurchasesServices";
import DeleteModal from "../../Components/Common/DeleteModal";
import { formatDateTimes } from "../../Components/Common/FormatDate";
import NumberWithCommas from "../../pages/Pages/Starter/NumberWithCommas";
import PurchaseBill from "./component/PurchaseBill";
import PurchaseForm from "./component/PurchaseForm";
import ResponsiveDataManagement from "../../Components/Common/ResponsiveManagementLayout";
import useServerSideSearch from "../../hooks/useServerSideSearch";
import StatusBadge from "../../Components/Common/Badge";

const Purchase = () => {
  const navigate = useNavigate();

  // ✅ Add payment status filter state
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");

  const {
    data: purchaseList,
    loading,
    totalRows,
    stats: apiStats,
    currentPage,
    pageSize,
    searchQuery,
    handleSearch,
    handlePaginationChange,
    refetch,
  } = useServerSideSearch(PurchaseService.getList);

  // Modal states
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);

  const toggleDetailModal = (purchase) => {
    setSelectedPurchase(purchase);
    setIsDetailOpen(!isDetailOpen);
  };

  // ✅ Filter data based on payment status
  const filteredPurchaseList = paymentStatusFilter
    ? purchaseList.filter(
        (item) =>
          item.paymentStatus?.toLowerCase() ===
          paymentStatusFilter.toLowerCase(),
      )
    : purchaseList;

  // ✅ Define payment status options
  const paymentStatusOptions = [
    { value: "paid", label: "Paid" },
    { value: "pending", label: "Pending" },
  ];

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (currentPage - 1) * pageSize + index + 1,
      sortable: false,
      width: "75px",
    },
    {
      name: "Supplier",
      selector: (row) => row.supplierName,
      cell: (row) => (
        <span
          style={{ cursor: "pointer", color: "#0d6efd" }}
          onClick={() => toggleDetailModal(row)}
          title={row.supplierName}
          className="text-truncate"
        >
          {row.supplierName}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Reference No",
      selector: (row) => row.refrenceNo,
      cell: (row) => {
        return (
          <span title={row.refrenceNo} className="text-truncate">
            {row.refrenceNo}
          </span>
        );
      },
      sortable: true,
    },
    {
      name: "Contact",
      selector: (row) => row.supplierContact,
      sortable: true,
    },
    {
      name: "Date",
      selector: (row) => formatDateTimes(row.purchaseDate),
      sortable: true,
    },

    {
      name: "Payment",
      selector: (row) => row.paymentStatus,
      cell: (row) => (
        <StatusBadge status={row.paymentStatus} type="payment" pill={true} />
      ),
      sortable: true,
      width: "130px",
      center: true,
    },
    {
      name: "Orders",
      selector: (row) => row?.items?.length,
      cell: (row) => row?.items?.length || 0,
      sortable: true,
      right: true,
    },
    {
      name: "Total Amt",
      selector: (row) => row.totalAmount,
      cell: (row) => <NumberWithCommas number={row.totalAmount} />,
      sortable: true,
      right: true,
    },
  ];

  const stats = [
    {
      title: "Total Purchases",
      value: apiStats?.totalPurchases || 0,
      color: "#0d6efd",
      icon: "bx bx-category",
    },
    {
      title: "Total Product Purchased",
      value: <NumberWithCommas number={apiStats?.totalItemsPurchased || 0} />,
      color: "#28a745",
      icon: "bx bx-check-circle",
    },
    {
      title: "Unpaid Purchases",
      value: apiStats?.unpaidPurchases || 0,
      color: "#17a2b8",
      icon: "bx bx-hide",
    },
    {
      title: "Supplier Count",
      value: apiStats?.uniqueSuppliersCount || 0,
      color: "#ffc107",
      icon: "bx bx-user",
    },
  ];

  const handleDelete = async (purchase) => {
    try {
      await PurchasesServices.delete(purchase.id);
      toast.success("Purchase deleted successfully");
      refetch();
      setDeleteModal(false);
    } catch (error) {
      console.error("Error deleting purchase:", error);
      toast.error(
        error?.response?.data?.message || "Failed to delete purchase",
      );
    }
  };

  const handleAdd = () => {
    navigate("/purchases/create");
  };

  const handleEdit = (purchase) => {
    navigate(`/purchases/edit/${purchase.id}`);
  };

  const handleView = (purchase) => {
    toggleDetailModal(purchase);
  };

  const handleDeleteClick = (purchase) => {
    setSelectedItem(purchase);
    setDeleteModal(true);
  };

  const handleMarkPaid = async (purchase) => {
    try {
      await PurchaseService.markBillPaid(purchase.id);
      toast.success("Purchase marked as paid successfully");
      refetch();
    } catch (error) {
      toast.error("Failed to mark as paid");
      console.error("Error marking paid:", error);
    }
  };

  // ✅ Handle payment status filter change
  const handlePaymentStatusChange = (value) => {
    setPaymentStatusFilter(value);
  };

  return (
    <>
      <ResponsiveDataManagement
        title="Purchase"
        pageTitle="Inventory Management"
        mobileCardTitle="Purchase List"
        data={filteredPurchaseList}
        columns={columns}
        stats={stats}
        searchKeys={["supplierName", "supplierContact", "paymentStatus"]}
        onAdd={handleAdd}
        onEdit={handleEdit}
        canEdit={(item) => item.paymentStatus?.trim().toLowerCase() !== "paid"}
        loading={loading}
        emptyStateMessage="No Purchases found"
        totalRows={totalRows}
        currentPage={currentPage}
        pageSize={pageSize}
        onSearch={handleSearch}
        searchTerm={searchQuery}
        onPaginationChange={handlePaginationChange}
        // ✅ Add select filter props
        onSelect={handlePaymentStatusChange}
        selectOptions={paymentStatusOptions}
        selectValue={paymentStatusFilter}
        selectPlaceholder="All Status"
        // selectLabel="Payment Status"
        customActions={[
          {
            label: "Mark as Paid",
            icon: "bx bx-check-circle",
            visible: (item) =>
              item.paymentStatus?.trim().toLowerCase() !== "paid",
            onClick: (item) => {
              handleMarkPaid(item);
            },
          },
        ]}
        mobileCardRenderer={(item, index) => (
          <Card
            key={item.id}
            className="shadow-sm rounded-3 border-0 border-start border-4 border-primary mb-3"
            onClick={() => handleView(item)}
          >
            <CardBody>
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <h6 className="fw-bold text-primary mb-2">
                    Purchase #{(currentPage - 1) * pageSize + index + 1}
                  </h6>
                  <p className="mb-1 text-muted small">
                    <strong>Date:</strong> {formatDateTimes(item.purchaseDate)}
                  </p>
                  <p className="mb-1 text-muted small">
                    <strong>Supplier:</strong> {item.supplierName}
                  </p>
                  <p className="mb-1 text-muted small">
                    <strong>Contact:</strong> {item.supplierContact}
                  </p>
                  <p className="mb-1 text-muted small">
                    <strong>Total Amount:</strong>{" "}
                    <NumberWithCommas number={item.totalAmount} />
                  </p>
                  <div className="d-flex gap-2 flex-wrap mt-2">
                    <StatusBadge status={item.paymentStatus} type="payment" />
                  </div>
                </div>

                <Dropdown
                  isOpen={openDropdownId === item.id}
                  toggle={() =>
                    setOpenDropdownId(
                      openDropdownId === item.id ? null : item.id,
                    )
                  }
                  direction="down"
                >
                  <DropdownToggle
                    color="light"
                    className="btn-sm rounded-circle shadow-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="ri-more-2-fill" />
                  </DropdownToggle>

                  <DropdownMenu end>
                    {item.paymentStatus?.trim().toLowerCase() !== "paid" && (
                      <DropdownItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(item);
                          setOpenDropdownId(null);
                        }}
                      >
                        <i className="ri-edit-line me-2" />
                        Edit
                      </DropdownItem>
                    )}

                    {item.paymentStatus?.trim().toLowerCase() !== "paid" && (
                      <DropdownItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkPaid(item);
                          setOpenDropdownId(null);
                        }}
                      >
                        <i className="bx bx-check-circle me-2" />
                        Mark as Paid
                      </DropdownItem>
                    )}
                  </DropdownMenu>
                </Dropdown>
              </div>
            </CardBody>
          </Card>
        )}
      />

      {/* Modals */}
      <PurchaseBill
        isOpen={isDetailOpen}
        toggle={() => setIsDetailOpen(false)}
        selectedPurchase={selectedPurchase}
        onPaymentDone={() => refetch()}
        fetchPurchase={refetch}
      />

      <PurchaseForm
        modal={addModal}
        toggleModal={() => {
          setAddModal(false);
          setSelectedItem(null);
        }}
        selectedItem={selectedItem}
        fetchPurchase={refetch}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={() => handleDelete(selectedItem)}
        onCloseClick={() => {
          setDeleteModal(false);
          setSelectedItem(null);
        }}
      />
    </>
  );
};

export default Purchase;
